import { useEffect, useMemo, useState } from 'react';
import { Check, History, X } from 'lucide-react';

import { work } from '../../lib/api.js';
import { useEditorStore } from '../../store/editorStore.js';
import { relativeTime } from '../common/index.jsx';
import { CaptionSurface } from './CaptionSurface.jsx';

/**
 * Everything the admin sees and the annotator must not: the original caption, the
 * pipeline's proposed spans, the verified missing details, and the edit history.
 *
 * The server already withholds these fields from annotator responses; this component
 * simply renders them when they are present.
 */
export function AdminExtras({ sample, draft, slot }) {
  if (!sample) return null;

  return (
    <div className="stack">
      <details className="disclosure">
        <summary>Original caption vs agent caption</summary>
        <div className="caption-compare">
          <div className="compare-col">
            <h4>Original ({sample.caption_source})</h4>
            <p className="caption-surface caption-body">{sample.original_caption}</p>
          </div>
          <div className="compare-col">
            <h4>Agent final caption</h4>
            <p className="caption-surface caption-body">{sample.agent_final_caption}</p>
          </div>
        </div>
      </details>

      {sample.final_spans?.length > 0 && (
        <details className="disclosure">
          <summary>
            Agent span decisions ({draft.spanDecisions.length}/{sample.final_spans.length} decided)
          </summary>
          <DecisionQueue sample={sample} draft={draft} slot={slot} />
        </details>
      )}

      {sample.missing_details?.length > 0 && (
        <details className="disclosure">
          <summary>
            Missing details ({draft.detailDecisions.length}/{sample.missing_details.length} decided)
          </summary>
          <MissingDetails sample={sample} draft={draft} slot={slot} />
        </details>
      )}

      <details className="disclosure">
        <summary>History and provenance</summary>
        <HistoryPanel sampleId={sample.sample_id} draft={draft} />
      </details>
    </div>
  );
}

function DecisionQueue({ sample, draft, slot }) {
  const setDecision = useEditorStore((s) => s.setDecision);
  const byIndex = useMemo(
    () => Object.fromEntries(draft.spanDecisions.map((d) => [d.span_index, d])),
    [draft.spanDecisions],
  );

  return (
    <div className="decision-rail">
      {sample.final_spans.map((span) => {
        const decision = byIndex[span.span_index]?.decision;
        return (
          <div key={span.span_index} className={`decision-card ${decision || ''}`}>
            <div className="decision-quote">“{span.span_text}”</div>
            <div className="decision-reason">
              <span className="chip">{span.final_label}</span>{' '}
              <span className="chip">{span.final_action}</span>
              {span.replacement && <> → “{span.replacement}”</>}
              {span.reason && <div style={{ marginTop: 4 }}>{span.reason}</div>}
            </div>
            <div className="decision-actions">
              <button
                type="button"
                className={`btn sm ${decision === 'confirmed' ? 'active' : ''}`}
                onClick={() => setDecision(slot, span.span_index, 'confirmed')}
              >
                <Check size={12} /> Confirm
              </button>
              <button
                type="button"
                className={`btn sm ${decision === 'rejected' ? 'active' : ''}`}
                onClick={() => setDecision(slot, span.span_index, 'rejected')}
              >
                <X size={12} /> Reject
              </button>
              <button
                type="button"
                className={`btn sm ${decision === 'wrong_label_or_action' ? 'active' : ''}`}
                title="The span is a real problem, but the agent's label, action or replacement is wrong"
                onClick={() => setDecision(slot, span.span_index, 'wrong_label_or_action')}
              >
                Wrong label/action
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MissingDetails({ sample, draft, slot }) {
  const setDetailDecision = useEditorStore((s) => s.setDetailDecision);
  const byIndex = useMemo(
    () => Object.fromEntries(draft.detailDecisions.map((d) => [d.detail_index, d])),
    [draft.detailDecisions],
  );

  return (
    <div className="decision-rail">
      {sample.missing_details.map((detail) => {
        const decision = byIndex[detail.detail_index]?.decision;
        return (
          <div key={detail.detail_index} className={`decision-card ${decision || ''}`}>
            <div className="decision-quote">{detail.detail_text}</div>
            <div className="decision-actions">
              <button
                type="button"
                className={`btn sm ${decision === 'confirmed' ? 'active' : ''}`}
                onClick={() => setDetailDecision(slot, detail.detail_index, 'confirmed')}
              >
                <Check size={12} /> Confirm
              </button>
              <button
                type="button"
                className={`btn sm ${decision === 'rejected' ? 'active' : ''}`}
                onClick={() => setDetailDecision(slot, detail.detail_index, 'rejected')}
              >
                <X size={12} /> Reject
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryPanel({ sampleId, draft }) {
  const [revisions, setRevisions] = useState(null);
  const owner = draft.provenance?.owner?.id;

  useEffect(() => {
    work
      .revisions(sampleId, owner)
      .then((r) => setRevisions(r.items || []))
      .catch(() => setRevisions([]));
  }, [sampleId, owner]);

  const provenance = draft.provenance;

  return (
    <div className="stack">
      {provenance && (
        <div className="banner">
          <span className="grow">
            Annotated by <strong>{provenance.original_annotator.display_name}</strong>
            {provenance.edited_by_someone_else && (
              <>
                {' '}· last edited by <strong>{provenance.last_edited_by.display_name}</strong>{' '}
                {relativeTime(provenance.last_edited_at)}
                {provenance.foreign_edit_count > 0 && (
                  <span className="chip warn" style={{ marginLeft: 8 }}>
                    {provenance.foreign_edit_count} edit
                    {provenance.foreign_edit_count === 1 ? '' : 's'} by someone else
                  </span>
                )}
              </>
            )}
          </span>
        </div>
      )}

      {revisions === null ? (
        <p className="small muted">Loading history…</p>
      ) : revisions.length === 0 ? (
        <p className="small muted">No history yet.</p>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>#</th>
              <th>When</th>
              <th>Who</th>
              <th>What</th>
            </tr>
          </thead>
          <tbody>
            {revisions.map((rev) => (
              <tr key={rev.rev_no}>
                <td className="num">{rev.rev_no}</td>
                <td className="nowrap">{relativeTime(rev.created_at)}</td>
                <td>
                  {rev.author_display_name}
                  {rev.is_foreign_edit && <span className="chip warn" style={{ marginLeft: 6 }}>not the author</span>}
                </td>
                <td className="small muted">
                  <span className="chip">{rev.op}</span>{' '}
                  {rev.changed_fields?.filter((f) => f !== '*').join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="tiny faint">
        <History size={11} style={{ verticalAlign: 'middle' }} /> The annotator's submitted
        version is frozen before anyone else edits it, so the original is always recoverable.
      </p>
    </div>
  );
}
